import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import PageHeader from "components/Common/PageHeader";
import CustomButton from "components/Common/ui/CustomButton";
import TableFilterDynamique, {
  FilterField,
} from "components/Common/TableFilter/TableFilterDynamique";

import ExamSessionModal from "../components/ExamSessionModal";
import ExamSessionList from "../components/PedagogyExamSessionList";

import {
  CreateExamSessionPayload,
  UpdateExamSessionPayload,
} from "../../../../types/markTypes";

import { useExamSessions } from "../hooks/usePeadgogyExamSessions";
import { useAbility } from "config/abilityContext";
import { Modal } from "antd";

const ExamSessionPage: React.FC = () => {
  const { t } = useTranslation();
  const ability = useAbility();

  const {
    examSessions,
    isSubmitting,
    isPublishing,
    handleCreateExamSession,
    handleUpdateExamSession,
    handleDeleteExamSession,
    handlePublishMarks,
    isLoading,
    fetchExamSessions,
    pagination,
  } = useExamSessions();

  const [modalVisible, setModalVisible] = useState(false);
  const [editingSession, setEditingSession] =
    useState<UpdateExamSessionPayload | null>(null);

  const [filters, setFilters] = useState<{ [key: string]: any }>({});

  const breadcrumbItems = [
    { label: t("dashboard"), path: "/dashboard" },
    { label: t("exam_sessions"), active: true },
  ];

  /* -------------------------------- FILTERS -------------------------------- */

  const filterFields: FilterField[] = useMemo(
    () => [
      { key: "name", label: t("session_name"), type: "text" },
      { key: "subject", label: t("subject"), type: "text" },
      {
        key: "semester",
        label: t("semester"),
        type: "select",
        options: [
          { label: t("semester_1"), value: "S1" },
          { label: t("semester_2"), value: "S2" },
        ],
      },
      {
        key: "start_date",
        label: t("start_date"),
        type: "range",
        rangeKeys: { start: "startDate", end: "endDate" },
      },
    ],
    [t]
  );

  const handleApplyFilters = (newFilters: { [key: string]: any }) => {
    setFilters({ ...newFilters });

    fetchExamSessions({
      ...newFilters,
      page: 1,
      limit: 10,
    });
  };

  const handleResetFilters = () => {
    setFilters({});
    fetchExamSessions({
      page: 1,
      limit: 10,
    });
  };


  const handleOpenCreate = () => {
    setEditingSession(null);
    setModalVisible(true);
  };

  const handleOpenEdit = (session: UpdateExamSessionPayload) => {
    setEditingSession(session);
    setModalVisible(true);
  };

  const handleSubmit = async (
    values: CreateExamSessionPayload | UpdateExamSessionPayload
  ) => {
    let res = false;

    if (editingSession) {
      res = await handleUpdateExamSession(
        values as UpdateExamSessionPayload
      );

      if (res) {
        setModalVisible(false);
        fetchExamSessions({
          ...filters,
          page: pagination?.page || 1,
          limit: pagination?.limit || 10,
        });
      }
    } else {
      res = await handleCreateExamSession(
        values as CreateExamSessionPayload
      );

      if (res) {
        setModalVisible(false);
        fetchExamSessions({
          ...filters,
          page: 1,
          limit: 10,
        });
      }
    }

    return res;
  };


  const handleDelete = async (id: string) => {
    Modal.confirm({
      title: t("delete_exam_session_title"),
      content: t("delete_exam_session_confirmation"),
      okText: t("delete_confirm"),
      okType: "danger",
      cancelText: t("cancel"),
      async onOk() {
        const res = await handleDeleteExamSession(id);
        if (!res) return;

        const currentPage = pagination?.page || 1;
        const pageSize = pagination?.limit || 10;
        const remainingItems = (pagination?.total || 0) - 1;

        const maxPageAfterDelete = Math.ceil(remainingItems / pageSize);

        const nextPage =
          currentPage > maxPageAfterDelete
            ? Math.max(maxPageAfterDelete, 1)
            : currentPage;

        fetchExamSessions({
          ...filters,
          page: nextPage,
          limit: pageSize,
        });
      },
    });
  };


  useEffect(() => {
    fetchExamSessions({
      page: 1,
      limit: 10,
    });
  }, []);


  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <PageHeader
          title={t("exam_sessions")}
          breadcrumbItems={breadcrumbItems}
        />

        <div className="d-flex mb-3 justify-content-end gap-2">
          {ability.can("create", "ExamSession") && (
            <CustomButton
              onClick={handleOpenCreate}
              variant="primary"
              size="md"
              className="btn-primary"
            >
              <i className="ti ti-plus me-2" />
              {t("create_exam_session")}
            </CustomButton>
          )}

          <TableFilterDynamique
            fields={filterFields}
            filters={filters}
            onApply={handleApplyFilters}
            onReset={handleResetFilters}
          />
        </div>

        <ExamSessionList
          examSessions={examSessions}
          loading={isLoading}
          isPublishing={isPublishing}
          onEdit={handleOpenEdit}
          onDelete={handleDelete}
          onPublishMarks={handlePublishMarks}
          dataPaginate={{
            current: pagination?.page,
            pageSize: pagination?.limit,
            total: pagination?.total,
          }}
          onChange={(pg: any) => {
            fetchExamSessions({
              ...filters,
              page: pg.current,
              limit: pg.pageSize,
            });
          }}
        />

        <ExamSessionModal
          visible={modalVisible}
          onClose={() => setModalVisible(false)}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          initialValues={editingSession ?? undefined}
          isEdit={!!editingSession}
        />
      </div>
    </div>
  );
};

export default ExamSessionPage;
